import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getUserData } from '../utils';
import { useReferrerStore } from './referrerStore';
import { hasPersonCommissionPayments } from '../utils/commissionPaymentCheck';
import type {
  DealCommissionOverride,
  DealCommissionOverrideFormData,
  EffectiveCommissionRate,
  CommissionHierarchyDisplay
} from '../types';

// P12: questo store gestisce SOLO gli override per-deal vivi (deal_user_commission_overrides per
// l'agente, snapshot su deal_referrers per i segnalatori) e la preview del rate via
// resolve_commission_rate — la stessa gerarchia usata dal motore in flusia-core.
// Le catene morte (client_commission_defaults, deal_commission_overrides, RPC
// get_effective_commission_rate) sono state rimosse col cutover V2.

interface DealCommissionStore {
  dealOverrides: DealCommissionOverride[];
  loading: boolean;
  error: string | null;

  // Deal commission overrides
  fetchDealCommissionOverrides: (dealId: string) => Promise<void>;
  createDealCommissionOverride: (dealId: string, data: DealCommissionOverrideFormData) => Promise<DealCommissionOverride | null>;

  // Commission preview (stessa gerarchia del motore)
  getEffectiveCommissionRate: (dealId: string, userId?: string, referrerId?: string, type?: 'user' | 'referrer') => Promise<EffectiveCommissionRate | null>;
  getCommissionHierarchy: (dealId: string, userId?: string, referrerId?: string, type?: 'user' | 'referrer') => Promise<CommissionHierarchyDisplay | null>;
}

// Mappa le source di resolve_commission_rate sulle categorie usate dalla UI
const mapResolveSource = (source: string): EffectiveCommissionRate['source'] => {
  switch (source) {
    case 'deal_override_service':
    case 'deal_override_deal':
    case 'deal_referrer_snapshot':
      return 'deal_override';
    case 'service_user_rate':
    case 'group_service_rate':
    case 'service_referrer_rate':
    case 'service_referrer_rate_2':
    case 'referrer_group_service_rate':
      return 'service_default';
    case 'group_base':
    case 'referrer_group_base':
    case 'user_default':
    case 'referrer_default':
      return 'user_default';
    default:
      return 'system_default';
  }
};

export const useDealCommissionStore = create<DealCommissionStore>((set, get) => ({
  dealOverrides: [],
  loading: false,
  error: null,

  fetchDealCommissionOverrides: async (dealId: string) => {
    set({ loading: true, error: null });
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) throw new Error("User not associated with an organization");

      // Fetch user commission overrides from deal_user_commission_overrides
      const { data: userOverrides, error: userError } = await supabase
        .from('deal_user_commission_overrides')
        .select('*')
        .eq('deal_id', dealId)
        .eq('organization_id', userData.organizationId)
        .order('created_at', { ascending: false });

      if (userError) throw userError;

      // Deduplicate: keep only the most recent record for each user_id + service_id combination
      const deduplicatedUserOverrides = userOverrides?.reduce((acc, override) => {
        const key = `${override.user_id}_${override.service_id || 'null'}`;
        if (!acc.has(key)) {
          acc.set(key, override);
        }
        return acc;
      }, new Map());
      const uniqueUserOverrides = deduplicatedUserOverrides ? Array.from(deduplicatedUserOverrides.values()) : [];

      // Convert to the display format used by DealCommissionSection.
      // Gli override segnalatore vivono come snapshot su deal_referrers e vengono caricati
      // direttamente dal componente via referrerStore (non passano più da questo store).
      const mergedOverrides: DealCommissionOverride[] = [];

      if (uniqueUserOverrides && uniqueUserOverrides.length > 0) {
        uniqueUserOverrides.forEach((override: any) => {
          // Determine the rate value based on commission type
          const commissionType = override.commission_type || 'percentage';
          const rateValue = commissionType === 'fixed'
            ? override.commission_fixed_amount
            : override.commission_rate;

          mergedOverrides.push({
            id: override.id,
            organization_id: override.organization_id,
            deal_id: override.deal_id,
            user_commission_rate: rateValue,
            user_commission_type: commissionType,
            user_commission_source: 'custom',
            referrer_id: null,
            referrer_commission_rate: null,
            referrer_commission_type: null,
            referrer_commission_source: null,
            override_reason: override.override_reason,
            is_active: true,
            created_at: override.created_at,
            updated_at: override.updated_at,
            created_by: override.created_by,
            updated_by: null
          });
        });
      }

      set({ dealOverrides: mergedOverrides });
    } catch (error) {
      console.error('Error fetching commission overrides:', error);
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createDealCommissionOverride: async (dealId: string, formData: DealCommissionOverrideFormData) => {
    try {
      const userData = await getUserData();
      if (!userData?.userId || !userData?.organizationId) throw new Error("Not authenticated");

      // Get the deal to find assigned_to user
      const { data: deal, error: dealError } = await supabase
        .from('deals')
        .select('assigned_to')
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;
      if (!deal?.assigned_to) {
        console.warn('Deal has no assigned user, skipping user commission override');
      }

      // Create user commission override if provided and deal has assigned user
      let userOverride = null;
      let agentBlocked = false;
      if (deal?.assigned_to) {
        // BLOCCO PER-PERSONA: Verifica se l'AGENTE ha pagamenti
        const agentHasPayments = await hasPersonCommissionPayments(
          dealId, deal.assigned_to, 'agent', userData.organizationId
        );

        if (agentHasPayments) {
          console.warn('[createDealCommissionOverride] Agent commission locked - skipping agent override');
          agentBlocked = true;
          // Continua comunque per salvare i referrer non pagati
        }

        // Solo se l'agente NON è bloccato, procediamo con le modifiche
        if (!agentBlocked) {
          if (formData.user_commission_rate !== undefined) {
            // First, delete any existing duplicates for this deal+user+service combination
            // This cleanup handles cases where the UNIQUE constraint doesn't exist in the DB
            await supabase
              .from('deal_user_commission_overrides')
              .delete()
              .eq('organization_id', userData.organizationId)
              .eq('deal_id', dealId)
              .eq('user_id', deal.assigned_to)
              .is('service_id', null);

            // Prepare data based on commission type (similar to referrer logic)
            const userCommissionData: {
              organization_id: string;
              deal_id: string;
              user_id: string;
              service_id: null;
              commission_rate: number;
              commission_type: string;
              commission_fixed_amount: number | null;
              override_reason?: string;
              created_by: string;
            } = {
              organization_id: userData.organizationId,
              deal_id: dealId,
              user_id: deal.assigned_to,
              service_id: null, // Apply to all services in the deal
              commission_rate: 0, // FIX: NOT NULL constraint - usa 0 come default
              commission_type: formData.user_commission_type || 'percentage',
              commission_fixed_amount: null,
              override_reason: formData.override_reason,
              created_by: userData.userId
            };

            // Set the correct field based on commission type
            if (formData.user_commission_type === 'fixed') {
              userCommissionData.commission_fixed_amount = formData.user_commission_rate ?? null;
              userCommissionData.commission_rate = 0; // FIX: NOT NULL constraint - usa 0 per commissioni fisse
            } else {
              userCommissionData.commission_rate = formData.user_commission_rate ?? 0; // FIX: NOT NULL constraint
              userCommissionData.commission_fixed_amount = null;
            }

            // Now insert the new record
            const { data, error } = await supabase
              .from('deal_user_commission_overrides')
              .insert(userCommissionData)
              .select()
              .single();

            if (error) throw error;
            userOverride = data;
          } else {
            // User cleared the field - delete any existing override to reset to default
            await supabase
              .from('deal_user_commission_overrides')
              .delete()
              .eq('organization_id', userData.organizationId)
              .eq('deal_id', dealId)
              .eq('user_id', deal.assigned_to)
              .is('service_id', null);
          }
        }
        // Se agentBlocked è true, non facciamo nulla per l'agente
      }

      // Create referrer commission overrides
      // NOTE: Referrer commissions are stored directly in deal_referrers table,
      // NOT in deal_user_commission_overrides (which has a FK to user_profiles)
      const referrerOverrides = [];
      for (const refOverride of formData.referrer_commission_overrides) {
        // FIX F-04 2026-07-10: se la % non è mai stata digitata (undefined) e non si tratta
        // di un reset esplicito (source 'client_default' impostato da handleReferrerCommissionChange
        // quando l'utente svuota il campo), NON sovrascrivere deal_referrers.commission_rate:
        // l'upsert con NULL cancellava il rate materializzato da addDealReferrer (es. % da gruppo).
        if (refOverride.commission_rate === undefined && refOverride.source !== 'client_default') {
          continue;
        }
        if (refOverride.commission_rate !== undefined || refOverride.commission_type) {
          // BLOCCO PER-PERSONA: Verifica se QUESTO segnalatore ha pagamenti
          const referrerHasPayments = await hasPersonCommissionPayments(
            dealId, refOverride.referrer_id, 'referrer', userData.organizationId
          );

          if (referrerHasPayments) {
            console.warn(`[createDealCommissionOverride] Referrer ${refOverride.referrer_id} commission locked - skipping`);
            continue; // Salta questo segnalatore ma procedi con gli altri
          }

          // Prepare update data based on commission type
          const updateData: {
            commission_rate?: number | null;
            commission_type?: string;
            commission_fixed_amount?: number | null;
          } = {};

          if (refOverride.commission_type === 'fixed') {
            // For fixed commissions: use commission_fixed_amount
            updateData.commission_type = 'fixed';
            updateData.commission_fixed_amount = refOverride.commission_rate ?? null;
            updateData.commission_rate = null; // commission_rate può essere NULL nel DB
          } else {
            // For percentage commissions (default)
            updateData.commission_type = 'percentage';
            updateData.commission_rate = refOverride.commission_rate ?? null; // null = usa default segnalatore
            updateData.commission_fixed_amount = null; // Clear fixed amount
          }

          // Use UPSERT to handle both insert and update cases
          // The unique constraint is on (deal_id, referrer_id)
          const { data, error } = await supabase
            .from('deal_referrers')
            .upsert({
              deal_id: dealId,
              referrer_id: refOverride.referrer_id,
              organization_id: userData.organizationId,
              ...updateData
            }, {
              onConflict: 'deal_id,referrer_id'
            })
            .select()
            .single();

          if (error) {
            console.warn('Could not upsert deal_referrers commission:', error);
          } else {
            referrerOverrides.push(data);
          }
        }
      }

      // Trigger refresh of DealReferrersView if any referrer commissions were saved
      if (referrerOverrides.length > 0) {
        useReferrerStore.getState().triggerDealReferrersRefresh();
      }

      // Refresh deal overrides
      await get().fetchDealCommissionOverrides(dealId);

      return userOverride || referrerOverrides[0] || null;
    } catch (error) {
      console.error('Error saving commission overrides:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  getEffectiveCommissionRate: async (dealId: string, userId?: string, referrerId?: string, type: 'user' | 'referrer' = 'user') => {
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) throw new Error("User not associated with an organization");

      const personId = type === 'referrer' ? referrerId : userId;
      if (!personId) return null;

      const { data, error } = await supabase.rpc('resolve_commission_rate', {
        p_organization_id: userData.organizationId,
        p_deal_id: dealId || null,
        p_service_id: null,
        p_person_id: personId,
        p_person_type: type
      });

      if (error) throw error;
      const row = data?.[0];
      if (!row) return null;

      return {
        rate: row.commission_type === 'fixed' ? (row.fixed_amount ?? row.rate ?? 0) : (row.rate ?? 0),
        commission_type: (row.commission_type === 'fixed' ? 'fixed' : 'percentage') as 'percentage' | 'fixed',
        source: mapResolveSource(row.source)
      };
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  getCommissionHierarchy: async (dealId: string, userId?: string, referrerId?: string, type: 'user' | 'referrer' = 'user') => {
    try {
      // Get effective rate (current active rate) — stessa gerarchia del motore
      const effectiveRate = await get().getEffectiveCommissionRate(dealId, userId, referrerId, type);
      if (!effectiveRate) return null;

      const hierarchy: CommissionHierarchyDisplay = {
        current_rate: effectiveRate.rate,
        current_type: effectiveRate.commission_type,
        current_source: effectiveRate.source,
        available_rates: {
          system_default: {
            rate: 0,
            commission_type: 'percentage',
            source: 'system_default'
          }
        }
      };

      return hierarchy;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  }
}));
